/* ===================================================================
   useBusiness() + useUpdateBusiness() -- business profile + brand voice
   (Brad, 2026-06-17).

   Read:  GET /api/v1/business  (profile + brand_voice blob, {} when unset)
   Write: PUT /api/v1/business  (any subset of profile fields + brand_voice)

   Settings -> Business profile + Brand voice bind to these. brand_voice
   feeds the AI draft prompts (drafts still never auto-post). The read hook
   is disabled until a Bearer rr_ token exists. After a save we invalidate
   the business query so both panels reflect the saved state.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type AdminTenantDashboard,
  type BusinessResponse,
  type BusinessUpdate,
  type PopulateFromPlaceResult,
  type ConfirmListingResult,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { adminDashboardKey } from './useAdminTenantDashboard';
import { qk } from './queryKeys';

export function useBusiness(): UseQueryResult<BusinessResponse, Error> {
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, select the business slice out of
  // the single admin dashboard bundle (brand_voice powers the goal ring).
  return useQuery({
    queryKey: admin ? adminDashboardKey(admin.clientId) : qk.business(),
    queryFn: ({ signal }) =>
      admin
        ? (api.getTenantDashboard(admin.clientId, signal) as Promise<unknown>)
        : (api.getBusiness(signal) as Promise<unknown>),
    select: admin ? (d) => (d as AdminTenantDashboard).business : undefined,
    enabled: admin ? true : hasToken,
    staleTime: 60_000,
  }) as UseQueryResult<BusinessResponse, Error>;
}

export interface UseUpdateBusinessResult {
  save: (patch: BusinessUpdate) => Promise<BusinessResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useUpdateBusiness(): UseUpdateBusinessResult {
  const qc = useQueryClient();
  const admin = useAdminView();
  // Hooks are always called (Rules of Hooks); we only swap the RETURNED object
  // when in an operator read-only view.
  const m = useMutation({
    mutationFn: (patch: BusinessUpdate) => api.updateBusiness(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.business() });
    },
  });
  // OPTION A read-only: an operator cannot save a client's business profile /
  // brand voice / monthly goal. Inert (defense-in-depth -- there is no admin
  // write endpoint to call).
  if (admin) {
    return {
      save: async () => {
        throw new Error('read_only_admin_view');
      },
      isPending: false,
      error: null,
    };
  }
  return {
    save: (patch) => m.mutateAsync(patch),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

/* ---------------------------------------------------------------------
   usePopulateFromPlace() -- POST /business/populate-from-place {place_id}.
   Auto-fills the profile from a Google place picked in the search box. On
   success we invalidate business (the profile form reloads with the pulled
   fields), me (google_location_known / business_name may have changed), and
   listingsHealth (a place_id stamp changes the NAP target). Returns the list
   of fields Google actually filled so the panel can confirm it plainly.
   --------------------------------------------------------------------- */
export interface UsePopulateFromPlaceResult {
  populate: (placeId: string) => Promise<PopulateFromPlaceResult>;
  isPending: boolean;
  error: Error | null;
}

export function usePopulateFromPlace(): UsePopulateFromPlaceResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (placeId: string) => api.populateBusinessFromPlace(placeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.business() });
      void qc.invalidateQueries({ queryKey: qk.me() });
      void qc.invalidateQueries({ queryKey: qk.listingsHealth() });
    },
  });
  return {
    populate: (placeId) => m.mutateAsync(placeId),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

/* ---------------------------------------------------------------------
   useConfirmListing() -- POST /integrations/google/confirm-listing {place_id}.
   The EXPLICIT affirmative action from the confirm-business card. On success
   the backend has saved confirmed_place_id AND imported >= 1 review, so the
   canonical google_connection_status can now reach 'connected'. We invalidate
   business (google_connection + google_candidate change), me (the status
   fields the whole app reads), listingsHealth (a confirmed place changes the
   NAP target), and widget (the review link/QR is only valid once connected).
   --------------------------------------------------------------------- */
export interface UseConfirmListingResult {
  confirm: (placeId: string) => Promise<ConfirmListingResult>;
  isPending: boolean;
  error: Error | null;
}

export function useConfirmListing(): UseConfirmListingResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (placeId: string) => api.confirmListing(placeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.business() });
      void qc.invalidateQueries({ queryKey: qk.me() });
      void qc.invalidateQueries({ queryKey: qk.listingsHealth() });
      void qc.invalidateQueries({ queryKey: qk.widget() });
    },
  });
  return {
    confirm: (placeId) => m.mutateAsync(placeId),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

/* ---------------------------------------------------------------------
   useRemoveBusiness() -- clears the connected Google business by sending
   PUT /business {google_location_id: ''} (empty string clears it). Wired for
   the NAP "Remove this business" control: after it clears, invalidate me +
   listingsHealth so the NAP page returns to the find-business empty state.
   --------------------------------------------------------------------- */
export interface UseRemoveBusinessResult {
  remove: () => Promise<BusinessResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useRemoveBusiness(): UseRemoveBusinessResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.updateBusiness({ google_location_id: '' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.business() });
      void qc.invalidateQueries({ queryKey: qk.me() });
      void qc.invalidateQueries({ queryKey: qk.listingsHealth() });
    },
  });
  return {
    remove: () => m.mutateAsync(),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
